import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Gauge, Loader2, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Sparkles, Wand2, Download } from "lucide-react";
import api from "@/lib/api";

const SECTION_LABELS = { experience: "Experience", skills: "Skills", education: "Education", formatting: "Formatting" };
const PERSONAS = [
  { id: "general", label: "General" },
  { id: "startup", label: "Startup" },
  { id: "corporate", label: "Corporate / MNC" },
  { id: "product", label: "Product Company" },
];

function scoreColor(score) {
  if (score >= 75) return "#34d399";
  if (score >= 50) return "#fbbf24";
  return "#fb7185";
}

export default function AtsScorePanel({ draft, setDraft, presetResult }) {
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showJd, setShowJd] = useState(false);
  const [jobDescription, setJobDescription] = useState("");
  const [persona, setPersona] = useState("general");
  const [rewriting, setRewriting] = useState(false);
  const [tailoring, setTailoring] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (presetResult) setResult(presetResult);
  }, [presetResult]);

  const checkScore = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/resume-builder/ats-score", { draft, job_description: jobDescription || undefined, persona });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add more resume details before checking your ATS score");
    } finally {
      setChecking(false);
    }
  };

  const tailorToJd = async () => {
    if (jobDescription.trim().length < 20) {
      toast.error("Paste a job description first (20+ characters) so we know what to tailor towards");
      return;
    }
    setTailoring(true);
    try {
      const { data } = await api.post("/resume-builder/tailor-to-jd", { draft, job_description: jobDescription });
      setDraft({ ...draft, summary: data.summary, skills: data.skills, experience: data.experience });
      toast.success("Summary, skills & experience tailored to this JD — re-check your score to see the impact");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not tailor your resume right now");
    } finally {
      setTailoring(false);
    }
  };

  const downloadPdfReport = async () => {
    setDownloadingPdf(true);
    try {
      const response = await api.post(
        "/resume-builder/ats-score/pdf",
        { result, full_name: draft?.full_name || "" },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(draft?.full_name || "Candidate").replace(/\s+/g, "_")}_ATS_Report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ATS report downloaded");
    } catch (e) {
      toast.error("Couldn't generate the PDF report right now");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const rewriteSummaryWithAi = async () => {
    setRewriting(true);
    try {
      const { data } = await api.post("/resume-builder/generate-summary", {
        target_role: draft.target_role, skills: draft.skills,
        experience_titles: draft.experience.map((e) => e.title).filter(Boolean),
        feedback: result.improvements,
      });
      setDraft({ ...draft, summary: data.summary });
      toast.success("Summary rewritten — re-check your score to see the impact");
    } catch (e) {
      toast.error("Could not rewrite summary right now");
    } finally {
      setRewriting(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-5" data-testid="ats-score-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 text-sm font-heading font-semibold text-white">
          <Gauge className="w-4 h-4 text-indigo-400" /> ATS Score Check
        </h3>
        <button onClick={() => setShowJd((v) => !v)} data-testid="ats-jd-toggle-btn" className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Match a JD {showJd ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {showJd && (
        <>
          <textarea
            value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} rows={3}
            placeholder="Paste a job description to also get a JD-match score..." data-testid="ats-jd-textarea"
            className="w-full mb-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-500"
          />
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ats-persona-selector">
            {PERSONAS.map((p) => (
              <button
                key={p.id} onClick={() => setPersona(p.id)} data-testid={`ats-persona-${p.id}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${persona === p.id ? "bg-indigo-500 text-white border-indigo-500" : "bg-white/5 text-slate-300 border-white/10 hover:border-indigo-400/40"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="flex gap-2">
        <button
          onClick={checkScore} disabled={checking} data-testid="ats-check-score-btn"
          className="flex-1 flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
        >
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gauge className="w-4 h-4" />}
          {checking ? "Analyzing…" : result ? "Re-check Score" : "Check ATS Score"}
        </button>
        {showJd && (
          <button
            onClick={tailorToJd} disabled={tailoring || jobDescription.trim().length < 20} data-testid="ats-tailor-to-jd-btn"
            className="flex-1 flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-40 disabled:cursor-not-allowed text-slate-200 text-sm font-semibold py-2.5 rounded-lg transition-colors"
          >
            {tailoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {tailoring ? "Tailoring…" : "Tailor Resume to JD"}
          </button>
        )}
      </div>

      {result && (
        <div className="mt-4 space-y-4" data-testid="ats-score-results">
          <div className="flex items-center gap-4">
            <div className="flex-1 text-center bg-white/5 rounded-xl py-3">
              <p className="text-2xl font-heading font-bold" style={{ color: scoreColor(result.ats_score) }} data-testid="ats-score-value">{result.ats_score}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold mt-0.5">ATS Score</p>
            </div>
            <div className="flex-1 text-center bg-white/5 rounded-xl py-3">
              <p className="text-2xl font-heading font-bold" style={{ color: scoreColor(result.overall_score) }} data-testid="ats-overall-score-value">{result.overall_score}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold mt-0.5">Overall Score</p>
            </div>
            {result.jd_match_score !== undefined && (
              <div className="flex-1 text-center bg-white/5 rounded-xl py-3">
                <p className="text-2xl font-heading font-bold" style={{ color: scoreColor(result.jd_match_score) }} data-testid="ats-jd-match-score-value">{result.jd_match_score}</p>
                <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold mt-0.5">JD Match</p>
              </div>
            )}
          </div>

          {result.seniority_level && (
            <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="ats-seniority-level">
              Your resume reads as: <span className="font-bold text-indigo-300 uppercase tracking-wide" data-testid="ats-seniority-level-value">{result.seniority_level}</span>
            </p>
          )}

          {(result.readability_score !== undefined || result.impact_score !== undefined) && (
            <div className="flex items-center gap-4">
              {result.readability_score !== undefined && (
                <div className="flex-1 text-center bg-white/5 rounded-xl py-2.5" data-testid="ats-readability-score-value">
                  <p className="text-lg font-heading font-bold" style={{ color: scoreColor(result.readability_score) }}>{result.readability_score}</p>
                  <p className="text-[9px] uppercase tracking-wide text-slate-400 font-bold mt-0.5">Readability</p>
                </div>
              )}
              {result.impact_score !== undefined && (
                <div className="flex-1 text-center bg-white/5 rounded-xl py-2.5" data-testid="ats-impact-score-value">
                  <p className="text-lg font-heading font-bold" style={{ color: scoreColor(result.impact_score) }}>{result.impact_score}%</p>
                  <p className="text-[9px] uppercase tracking-wide text-slate-400 font-bold mt-0.5">Bullets w/ Metrics</p>
                </div>
              )}
            </div>
          )}

          <button
            onClick={downloadPdfReport} disabled={downloadingPdf} data-testid="ats-download-pdf-btn"
            className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-60 text-slate-200 text-xs font-semibold py-2.5 rounded-lg transition-colors"
          >
            {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {downloadingPdf ? "Generating…" : "Download PDF Report"}
          </button>

          <div className="space-y-2">
            {Object.entries(result.section_scores || {}).map(([key, val]) => (
              <div key={key} data-testid={`ats-section-score-${key}`}>
                <div className="flex justify-between text-xs text-slate-400 mb-1">
                  <span>{SECTION_LABELS[key] || key}</span><span className="text-white font-semibold">{val}</span>
                </div>
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${val}%`, backgroundColor: scoreColor(val) }} />
                </div>
              </div>
            ))}
          </div>

          {result.matched_keywords?.length > 0 && (
            <div data-testid="ats-matched-keywords">
              <p className="text-xs font-semibold text-emerald-400 mb-1.5">Matched Keywords</p>
              <div className="flex flex-wrap gap-1.5">
                {result.matched_keywords.map((k) => <span key={k} className="text-[11px] bg-emerald-400/10 text-emerald-300 px-2 py-0.5 rounded-full">{k}</span>)}
              </div>
            </div>
          )}

          {result.missing_keywords?.length > 0 && (
            <div data-testid="ats-missing-keywords">
              <p className="text-xs font-semibold text-rose-400 mb-1.5">Missing Keywords</p>
              <div className="flex flex-wrap gap-1.5">
                {result.missing_keywords.map((k) => <span key={k} className="text-[11px] bg-rose-400/10 text-rose-300 px-2 py-0.5 rounded-full">{k}</span>)}
              </div>
            </div>
          )}

          {result.improvements?.length > 0 && (
            <div data-testid="ats-improvements">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs font-semibold text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Improvements</p>
                {setDraft && (
                  <button onClick={rewriteSummaryWithAi} disabled={rewriting} data-testid="ats-fix-summary-with-ai-btn" className="flex items-center gap-1 text-[11px] font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-60">
                    {rewriting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Fix Summary with AI
                  </button>
                )}
              </div>
              <ul className="space-y-1">
                {result.improvements.map((s, i) => <li key={i} className="text-xs text-slate-300 flex gap-1.5"><span className="text-amber-400">•</span>{s}</li>)}
              </ul>
            </div>
          )}

          {result.strengths?.length > 0 && (
            <div data-testid="ats-strengths">
              <p className="text-xs font-semibold text-indigo-400 mb-1.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Strengths</p>
              <ul className="space-y-1">
                {result.strengths.map((s, i) => <li key={i} className="text-xs text-slate-300 flex gap-1.5"><span className="text-indigo-400">•</span>{s}</li>)}
              </ul>
            </div>
          )}

          {result.summary && <p className="text-xs text-slate-400 italic border-t border-white/10 pt-3">{result.summary}</p>}
        </div>
      )}
    </div>
  );
}
